import { ReactNode, useState, useCallback } from 'react';
import { Transition } from '@headlessui/react';
import { cn } from '../../utils';
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    FloatingPortal,
    Placement,
} from '@floating-ui/react';

type Position =
    | 'topLeft'
    | 'top'
    | 'topRight'
    | 'bottomLeft'
    | 'bottom'
    | 'bottomRight'
    | 'leftTop'
    | 'left'
    | 'leftBottom'
    | 'rightTop'
    | 'right'
    | 'rightBottom';

type Theme = 'light' | 'dark';

type TooltipProps = {
    content: ReactNode;
    children: ReactNode;
    position?: Placement;
    theme?: Theme;
    overlayClass?: string;
    fontSize?: 'sm' | 'base' | 'lg' | 'xl';
    inModal?: boolean;
};

const positionToPlacement: { [Key in Position | Placement]: Placement } = {
    top: 'top',
    left: 'left',
    bottom: 'bottom',
    right: 'right',
    topLeft: 'top-start',
    topRight: 'top-end',
    bottomLeft: 'bottom-start',
    bottomRight: 'bottom-end',
    leftTop: 'left-start',
    leftBottom: 'left-end',
    rightTop: 'right-start',
    rightBottom: 'right-end',
    'top-start': 'top-start',
    'top-end': 'top-end',
    'bottom-start': 'bottom-start',
    'bottom-end': 'bottom-end',
    'left-start': 'left-start',
    'left-end': 'left-end',
    'right-start': 'right-start',
    'right-end': 'right-end',
};

const deprecatedPositions = [
    'topLeft',
    'topRight',
    'bottomLeft',
    'bottomRight',
    'leftTop',
    'leftBottom',
    'rightTop',
    'rightBottom',
];

export default function Tooltip({
    content,
    children,
    position = 'right',
    theme = 'dark',
    overlayClass,
    fontSize = 'base',
    inModal = false,
}: TooltipProps) {
    const [isVisible, setIsVisible] = useState(false);

    if (
        typeof position === 'string' &&
        deprecatedPositions.includes(position)
    ) {
        console.warn(
            `Tooltip Component: The position "${position}" is deprecated. Switch to "${positionToPlacement[position]}".`,
        );
        position = positionToPlacement[position];
    }

    const { refs, floatingStyles } = useFloating({
        placement: position,
        open: isVisible,
        middleware: [
            offset({
                mainAxis: 8,
            }),
            flip(),
        ],
        whileElementsMounted: autoUpdate,
    });

    const showTooltip = useCallback(() => {
        setIsVisible(true);
    }, []);

    const hideTooltip = useCallback(() => {
        setIsVisible(false);
    }, []);

    return (
        <>
            <div
                ref={refs.setReference}
                data-testid="tooltip"
                className="alli-relative alli-inline-block"
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}>
                {children}
            </div>
            <FloatingPortal>
                <Transition
                    show={isVisible}
                    className={cn('alli-relative alli-z-30', {
                        'alli-z-50': inModal,
                    })}
                    enter="alli-transition-opacity alli-duration-300"
                    enterFrom="alli-opacity-0"
                    enterTo="alli-opacity-100"
                    leave="alli-transition-opacity alli-duration-200 alli-delay-200"
                    leaveFrom="alli-opacity-100"
                    leaveTo="alli-opacity-0">
                    <div
                        ref={refs.setFloating}
                        className={cn(
                            'alli-relative alli-z-50 alli-whitespace-nowrap alli-rounded-md alli-p-2 alli-shadow-[0_0_10px_rgba(0,0,0,0.2)]',
                            {
                                'alli-bg-blue-gray-900 alli-text-white':
                                    theme === 'dark',
                                'alli-bg-white alli-text-blue-gray-700':
                                    theme === 'light',
                                'alli-text-sm': fontSize === 'sm',
                                'alli-text-base': fontSize === 'base',
                                'alli-text-lg': fontSize === 'lg',
                                'alli-text-xl': fontSize === 'xl',
                            },
                            overlayClass,
                        )}
                        style={floatingStyles}
                        onMouseEnter={showTooltip}
                        onMouseLeave={hideTooltip}>
                        {content}
                    </div>
                </Transition>
            </FloatingPortal>
        </>
    );
}
